import { useQuery } from 'react-query';
import type { ReportFilterConfig } from '@reports/domain/value-objects';
import type { FilterOptionsViewDto } from '@reports/application/dtos';
import type { ReportType } from '@reports/domain/types';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getFiltersUseCases } from '@reports/composition';

export type UseFilterCatalogProps = {
  filterConfigs: ReadonlyArray<ReportFilterConfig>;
  reportType: ReportType;
  enabled?: boolean;
};

/**
 * Hook to fetch filter catalog data
 *
 * Uses the FetchFilterCatalogUseCase to fetch filter catalog data from the API.
 * Gets filters use cases from composition root.
 *
 * @param {UseFilterCatalogProps} props - Hook props
 * @param {ReadonlyArray<ReportFilterConfig>} props.filterConfigs - Filter configurations from report definition
 * @param {boolean} props.enabled - Whether the hook is enabled
 * @returns {Object} Filter catalog data and state
 *
 * @example
 * const { filterCatalogData, filterCatalogState } = useFilterCatalog({
 *   filterConfigs: reportDefinition.filters,
 *   enabled: true,
 * });
 */
export const useFilterCatalog = ({ filterConfigs, reportType, enabled = true }: UseFilterCatalogProps) => {
  const { t } = useTranslation(['reports']);
  const { fetchFilterCatalog } = getFiltersUseCases();

  const { data, error, refetch, isError, isFetching, isSuccess } = useQuery<FilterOptionsViewDto>({
    queryKey: ['reports', 'filter-catalog', reportType],
    queryFn: () => fetchFilterCatalog.execute(filterConfigs),
    enabled: enabled && filterConfigs.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: false,
    onError: (_error) => {
      toast.error(t('reportCommon:messages.errorFetchingFilterCatalog'));
    },
  });

  return {
    filterCatalogData: data,
    filterCatalogError: error instanceof Error ? error.message : null,
    filterCatalogState: { isFetching, isError, isSuccess },
    refetchFilterCatalog: refetch,
  };
};
